import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Folder, Calendar, ArrowRight, Facebook, Linkedin, Mail, Link as LinkIcon } from 'lucide-react';
import axios from 'axios';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATIC_HEADER = { cta_url: "/donate" };
const STATIC_FOOTER = { contact_email: "info@securethevotemd.com" };

const ShareButton = ({ href, icon, label, onClick, copied = false, className = '' }) => {
  const content = (
    <>
      {icon}
      <span>{copied ? 'Copied!' : label}</span>
    </>
  );

  const baseClassName = `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 ${className}`;

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={baseClassName}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className={baseClassName}>
      {content}
    </button>
  );
};

const BlogPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    axios.get(`${API_URL}/api/posts/${slug}`)
      .then(res => {
        if (res.data?.external_url) {
          window.location.replace(res.data.external_url);
          return;
        }
        setPost(res.data);
        setLoading(false);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  const date = post?.published_at ? new Date(post.published_at) : null;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || 'Secure the Vote Maryland';
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);

  const shareLinks = [
    {
      label: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: <Facebook size={16} />,
      className: 'bg-[#1877F2] text-white hover:bg-[#1669d9] shadow-sm hover:shadow-md',
    },
    {
      label: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      icon: <Linkedin size={16} />,
      className: 'bg-[#0A66C2] text-white hover:bg-[#0958a8] shadow-sm hover:shadow-md',
    },
    {
      label: 'Email',
      href: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
      icon: <Mail size={16} />,
      className: 'bg-[#9E1B32] text-white hover:bg-[#86162a] shadow-sm hover:shadow-md',
    },
  ];

  const handleCopyLink = async () => {
    if (!shareUrl || !navigator?.clipboard) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      <TopBar />
      <Header config={STATIC_HEADER} />

      {/* Hero */}
      <div className="relative pt-[50px]" style={{ backgroundColor: '#9E1B32' }}>
        <div className="py-12 sm:py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link
                to="/news"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition-colors"
                data-testid="back-to-news"
              >
                <ArrowLeft size={14} />
                Back to News
              </Link>

              {!loading && post && (
                <>
                  <div className="flex items-center gap-3 text-white/70 text-sm mb-4">
                    {post.category && (
                      <span className="flex items-center gap-1.5">
                        <Folder size={14} />
                        {post.category}
                      </span>
                    )}
                    {date && (
                      <span className="flex items-center gap-1.5">
                        <Calendar size={14} />
                        {date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                  </div>

                  <h1
                    className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                    data-testid="blog-title"
                  >
                    {post.title}
                  </h1>

                  <motion.div
                    className="w-16 h-1 bg-[#F6BF58] mt-6"
                    initial={{ scaleX: 0, originX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  />
                </>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="py-10 sm:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          {loading && (
            <div className="space-y-4">
              <div className="h-6 bg-slate-100 rounded animate-pulse w-3/4" />
              <div className="h-4 bg-slate-100 rounded animate-pulse w-full" />
              <div className="h-4 bg-slate-100 rounded animate-pulse w-5/6" />
              <div className="h-64 bg-slate-100 rounded animate-pulse w-full mt-6" />
              <div className="h-4 bg-slate-100 rounded animate-pulse w-full" />
              <div className="h-4 bg-slate-100 rounded animate-pulse w-2/3" />
            </div>
          )}

          {notFound && (
            <div className="text-center py-16" data-testid="blog-not-found">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Article Not Found</h2>
              <p className="text-slate-600 mb-6">This article doesn't exist or may have been removed.</p>
              <Link to="/news" className="text-[#9E1B32] font-semibold hover:underline inline-flex items-center gap-2">
                <ArrowLeft size={14} /> Back to News
              </Link>
            </div>
          )}

          {post && (
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              data-testid="blog-content"
            >
              {post.featured_image && (
                <img
                  src={post.featured_image}
                  alt={post.title}
                  className="w-full rounded-lg shadow-md mb-10"
                  data-testid="blog-featured-image"
                />
              )}

              <div
                className="prose prose-lg max-w-none
                  prose-headings:text-slate-900 prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-slate-600 prose-p:leading-relaxed prose-p:mb-4
                  prose-a:text-[#9E1B32] prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-slate-900
                  prose-ul:my-4 prose-li:text-slate-600 prose-li:leading-relaxed
                  prose-ol:my-4
                  prose-img:rounded-lg prose-img:shadow-md
                  prose-hr:my-8 prose-hr:border-slate-200"
                dangerouslySetInnerHTML={{ __html: post.content }}
                data-testid="blog-body"
              />

              <div className="mt-10 rounded-xl bg-slate-50 p-5 border border-slate-200" data-testid="blog-share-buttons">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-base font-semibold text-slate-900">Share this article</h3>
                  <div className="flex flex-wrap gap-2">
                    {shareLinks.map((link) => (
                      <ShareButton
                        key={link.label}
                        href={link.href}
                        icon={link.icon}
                        label={link.label}
                        className={link.className}
                      />
                    ))}
                    <ShareButton
                      icon={<LinkIcon size={16} />}
                      label="Copy Link"
                      onClick={handleCopyLink}
                      copied={copied}
                      className="border border-slate-300 bg-white text-slate-700 hover:border-[#9E1B32] hover:text-[#9E1B32]"
                    />
                  </div>
                </div>
              </div>

              {/* Bottom nav */}
              <div className="mt-12 pt-8 border-t border-slate-200">
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 text-[#9E1B32] font-semibold hover:gap-3 transition-all"
                  data-testid="back-to-news-bottom"
                >
                  <ArrowLeft size={16} />
                  Back to All Articles
                </Link>
              </div>
            </motion.article>
          )}
        </div>
      </main>

      <Footer config={STATIC_FOOTER} />
    </div>
  );
};

export default BlogPage;
